/**
 * Application Routing Configuration
 *
 * This file defines all routes for the application using React Router.
 * It includes both public and protected routes.
 */
import { createBrowserRouter, RouteObject } from 'react-router-dom'
import AppLayout from '../components/layout/app-layout'
import HomePage from '../pages/home'
import AboutPage from '../pages/about'
import ErrorBoundary from '../components/error/error-boundary'

// Auth pages
import { LoginPage } from '@/pages/auth/login-page'
import { SignupPage } from '@/pages/auth/signup-page'
import { ResetPasswordPage } from '@/pages/auth/reset-password-page'
import { EmailVerificationPage } from '@/pages/auth/email-verification-page'
import { AccountPage } from '@/pages/profile/account-page'
import { SettingsPage } from '@/pages/profile/settings-page'
import { ProtectedRoute } from '@/components/auth/protected-route'
import CalendarPage from '@/pages/calendar'
import CreateEventPage from '@/pages/calendar/create'

// Meetup pages
import MeetupsListPage from '@/pages/meetups'
import MeetupDetailWrapper from '@/pages/meetups/meetup-detail-wrapper'
import CreateMeetupPage from '@/pages/meetups/create'
import EditPageWrapper from '@/pages/meetups/edit/edit-page-wrapper'
import MeetupCalendarPage from '@/pages/meetups/[id]/calendar'

// Define route objects
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <AppLayout />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      // Auth routes
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'signup',
        element: <SignupPage />,
      },
      {
        path: 'forgot-password',
        element: <ResetPasswordPage />,
      },
      {
        path: 'email-verification',
        element: <EmailVerificationPage />,
      },
      // Protected routes
      {
        path: 'account',
        element: (
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      // Calendar routes
      {
        path: 'calendar',
        element: (
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'calendar/create',
        element: (
          <ProtectedRoute>
            <CreateEventPage />
          </ProtectedRoute>
        ),
      },
      // Meetup routes
      {
        path: 'meetups',
        element: (
          <ProtectedRoute>
            <MeetupsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meetups/create',
        element: (
          <ProtectedRoute>
            <CreateMeetupPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meetups/:id',
        element: (
          <ProtectedRoute>
            <MeetupDetailWrapper />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meetups/:id/calendar',
        element: (
          <ProtectedRoute>
            <MeetupCalendarPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meetups/:id/edit',
        element: (
          <ProtectedRoute>
            <EditPageWrapper />
          </ProtectedRoute>
        ),
      },
      // Redirect from old profile path to account
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        ),
      },
      // Additional routes will be added here in future phases
      {
        path: '*',
        element: (
          <div className="py-12 text-center">
            <h2 className="text-2xl font-bold mb-4">Page Not Found</h2>
            <p className="text-muted-foreground">The page you're looking for doesn't exist.</p>
          </div>
        ),
      },
    ],
  },
]

// Create and export the router
export const router = createBrowserRouter(routes)

// Export the routes for testing or other uses
export default routes
